export interface GenerationReaction {
  gasId: string;
  reactants: string[];
  conditions: string[];
  equation: string;
  apparatus: string[];
  notes: string;
}

export const generationReactions: GenerationReaction[] = [
  {
    gasId: 'h2',
    reactants: ['Zn', 'dilute HCl'],
    conditions: ['test tube', 'acid reacts with metal'],
    equation: 'Zn + 2HCl -> ZnCl₂ + H₂',
    apparatus: ['test tube', 'delivery tube', 'gas jar or syringe'],
    notes: 'Hydrogen is colourless and highly flammable. Test with a lit splint.',
  },
  {
    gasId: 'o2',
    reactants: ['H₂O₂', 'MnO₂'],
    conditions: ['catalyst present'],
    equation: '2H₂O₂ -> 2H₂O + O₂',
    apparatus: ['test tube', 'delivery tube'],
    notes: 'Oxygen supports combustion and relights a glowing splint.',
  },
  {
    gasId: 'co2',
    reactants: ['CaCO₃', 'dilute HCl'],
    conditions: ['marble chips and acid'],
    equation: 'CaCO₃ + 2HCl -> CaCl₂ + H₂O + CO₂',
    apparatus: ['test tube', 'delivery tube', 'limewater'],
    notes: 'Carbon dioxide turns limewater milky.',
  },
  {
    gasId: 'nh3',
    reactants: ['NH₄Cl', 'NaOH'],
    conditions: ['heat gently'],
    equation: 'NH₄Cl + NaOH -> NaCl + NH₃ + H₂O',
    apparatus: ['test tube', 'delivery tube', 'damp litmus'],
    notes: 'Ammonia is basic and turns damp red litmus blue.',
  },
  {
    gasId: 'cl2',
    reactants: ['MnO₂', 'concentrated HCl'],
    conditions: ['heat strongly in a fume cupboard'],
    equation: 'MnO₂ + 4HCl -> MnCl₂ + Cl₂ + 2H₂O',
    apparatus: ['test tube', 'fume cupboard', 'damp litmus'],
    notes: 'Chlorine is a pale yellow-green gas and bleaches litmus.',
  },
  {
    gasId: 'so2',
    reactants: ['Na₂SO₃', 'dilute HCl'],
    conditions: ['acidification'],
    equation: 'Na₂SO₃ + 2HCl -> 2NaCl + SO₂ + H₂O',
    apparatus: ['test tube', 'delivery tube', 'dichromate paper'],
    notes: 'Sulfur dioxide turns acidified dichromate green.',
  },
  {
    gasId: 'hcl',
    reactants: ['NaCl', 'concentrated H₂SO₄'],
    conditions: ['heated gently'],
    equation: 'NaCl + H₂SO₄ -> NaHSO₄ + HCl',
    apparatus: ['test tube', 'glass rod', 'ammonia'],
    notes: 'Hydrogen chloride is acidic and forms white fumes with ammonia.',
  },
  {
    gasId: 'no2',
    reactants: ['Cu', 'concentrated HNO₃'],
    conditions: ['heat gently'],
    equation: 'Cu + 4HNO₃ -> Cu(NO₃)₂ + 2NO₂ + 2H₂O',
    apparatus: ['test tube', 'delivery tube', 'blue litmus'],
    notes: 'Nitrogen dioxide is a brown gas that turns blue litmus red.',
  },
  {
    gasId: 'n2',
    reactants: ['No reaction', 'reference gas'],
    conditions: ['control gas only'],
    equation: 'N₂',
    apparatus: ['gas jar', 'splint'],
    notes: 'Nitrogen is inert; it extinguishes a burning splint and does not support combustion.',
  },
  {
    gasId: 'h2s',
    reactants: ['FeS', 'dilute HCl'],
    conditions: ['acidification', 'fume cupboard'],
    equation: 'FeS + 2HCl -> FeCl₂ + H₂S',
    apparatus: ['test tube', 'lead acetate paper'],
    notes: 'Hydrogen sulfide smells like rotten eggs and blackens lead acetate paper.',
  },
];

export function getReaction(gasId: string): GenerationReaction {
  const match = generationReactions.find((reaction) => reaction.gasId === gasId.toLowerCase());
  if (!match) {
    throw new RangeError(`No generation reaction defined for ${gasId}`);
  }
  return match;
}
